import { useFormik } from "formik";
import { Form } from "reactstrap";

import { HeadingWithBackArrow } from "components/HeadingWithBackArrow";
import { CustomButton } from "components/CustomButton";
import { LoaderFallbackUI } from "components/LoaderFallbackUI";
import { validationSchema } from "../validation";
import typography from "../typography";
import QuestionRow from "./QuestionRow";
import plusIcon from "assets/images/app/common/plus-icon.svg";

const emptyQuestion = () => ({
    content: "",
    type: "TEXT",
    requiredField: true,
    answerOptions: [],
});

// Shared by Add and Edit — the two only differ in initialValues, what onSubmit does,
// and whether question content is locked (once a form has real submissions, only the
// backend enforces that; questionsLocked here just reflects that back into the UI so
// admins aren't surprised by a rejected save).
export default function FeedbackFormBuilder({
    initialValues = { questions: [emptyQuestion()] },
    onSubmit = async () => {},
    questionsLocked = false,
    loading = false,
    isSaving = false,
    backButtonHandler = () => {},
    heading = "",
}) {
    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            await onSubmit(values);
        },
    });

    const setQuestionField = (index, field, value) => {
        const questions = [...formik.values.questions];
        questions[index] = { ...questions[index], [field]: value };
        if (field === "type" && value === "OPTIONS" && !questions[index].answerOptions.length) {
            questions[index].answerOptions = [{ content: "" }, { content: "" }];
        }
        formik.setFieldValue("questions", questions);
    };

    const addQuestion = () => {
        formik.setFieldValue("questions", [...formik.values.questions, emptyQuestion()]);
    };

    const removeQuestion = (index) => {
        const questions = formik.values.questions.filter((_, i) => i !== index);
        formik.setFieldValue("questions", questions);
    };

    const addOption = (questionIndex) => {
        const questions = [...formik.values.questions];
        questions[questionIndex] = {
            ...questions[questionIndex],
            answerOptions: [...questions[questionIndex].answerOptions, { content: "" }],
        };
        formik.setFieldValue("questions", questions);
    };

    const changeOption = (questionIndex, optionIndex, value) => {
        const questions = [...formik.values.questions];
        const answerOptions = [...questions[questionIndex].answerOptions];
        answerOptions[optionIndex] = { content: value };
        questions[questionIndex] = { ...questions[questionIndex], answerOptions };
        formik.setFieldValue("questions", questions);
    };

    const removeOption = (questionIndex, optionIndex) => {
        const questions = [...formik.values.questions];
        questions[questionIndex] = {
            ...questions[questionIndex],
            answerOptions: questions[questionIndex].answerOptions.filter(
                (_, i) => i !== optionIndex
            ),
        };
        formik.setFieldValue("questions", questions);
    };

    if (loading) return <LoaderFallbackUI isLoader={loading} />;

    return (
        <>
            <div className="edit-notification-heading">
                <HeadingWithBackArrow
                    backArrowClickHandler={backButtonHandler}
                    headingTitle={heading}
                    showToggle={false}
                />
                <div className="edit-notification-heading-button">
                    <CustomButton
                        title={isSaving ? typography.SAVING : typography.SAVE}
                        className="edit-notification-update-button"
                        onClick={formik.handleSubmit}
                        type="submit"
                        loading={isSaving}
                    />
                </div>
            </div>

            {questionsLocked && (
                <div className="feedback-questions-locked-banner mb-3">
                    {typography.QUESTIONS_LOCKED_MESSAGE}
                </div>
            )}

            <Form onSubmit={formik.handleSubmit} className="feedback-form-builder">
                {formik.values.questions.map((question, index) => (
                    <QuestionRow
                        key={index}
                        question={question}
                        index={index}
                        disabled={questionsLocked}
                        errors={formik.errors}
                        touched={formik.touched}
                        onChange={setQuestionField}
                        onRemove={removeQuestion}
                        onAddOption={addOption}
                        onOptionChange={changeOption}
                        onRemoveOption={removeOption}
                        canRemove={formik.values.questions.length > 1}
                    />
                ))}

                {!questionsLocked && (
                    <div
                        role="button"
                        className="feedback-add-question mb-3"
                        onClick={addQuestion}
                    >
                        <img src={plusIcon} alt="add question" /> {typography.ADD_QUESTION}
                    </div>
                )}
            </Form>
        </>
    );
}
